/**
 * API Shim — stands in for Electron's `window.api` when the desktop renderer
 * runs in a phone browser.
 *
 * The mobile app boots the *same* React app the desktop does, so the shim has
 * to be a faithful replacement for the preload bridge: same property tree,
 * same call signatures, same return contracts (a method returns a Promise, a
 * subscription returns its unsubscribe). It's built from PRELOAD_SURFACE —
 * generated from the preload itself by scripts/gen-preload-surface.mjs — so a
 * method added to the desktop reaches mobile the moment the surface is
 * regenerated, on the right channel, at the right nesting depth.
 *
 *   window.api.gitStatus(dir)            → relay.request('git-status', [dir])
 *   window.api.claudeUsage.getSnapshot() → relay.request('claudeUsage:snapshot', [])
 *   window.api.onSessionsChanged(cb)     → cb on every mirrored 'sessions-changed'
 *
 * Requests are IPC channels invoked on the desktop by electron/main/relay.ts.
 * Events arrive as `ipc-event` frames: the desktop mirrors every
 * main→renderer send over the relay, so subscriptions behave exactly as they
 * do under Electron, positional arguments included.
 */

import { PRELOAD_SURFACE } from './preload-surface'

/**
 * The transport a shim talks over.
 *
 * This is the exact subset of mobile/src/lib/relay.ts's RelayClient that the
 * shim consumes. Naming it here is what lets a shim be built against
 * *something other than* the mobile singleton — a per-tab share session, say —
 * without app/src needing to reach into mobile/.
 *
 * Everything in this file MUST route through the injected instance. A single
 * leftover reference to a module-level singleton would bind that one facility
 * (pty fan-out, presence) to the wrong channel with no type error to catch it.
 */
export interface ApiShimTransport {
  request<T = any>(message: { type: string; args?: unknown[] }, timeoutMs?: number): Promise<T>
  send(message: unknown): void
  on(event: string, callback: (data: any) => void): void
  off(event: string, callback: (data: any) => void): void
}

/**
 * Surface that can only mean something on the machine itself — native
 * dialogs, window chrome, the overlay, the updater. Each entry is the value
 * the call resolves to on mobile, so callers get a sane answer instead of a
 * request that sits there until it times out.
 */
const DESKTOP_ONLY: Record<string, unknown> = {
  // Window chrome — the phone has no title bar.
  isMaximized: false,
  setWindowBackground: undefined,
  takeScreenshot: undefined,
  // Native dialogs would open on a desktop nobody is sitting at.
  showInFolder: undefined,
  selectDirectory: undefined,
  pickFilesForSkill: undefined,
  // Overlay attaches to native windows.
  overlayGetWindows: [],
  overlayAttach: undefined,
  overlayAttachToFrontmost: undefined,
  overlayDetach: undefined,
  overlayShow: undefined,
  overlayHide: undefined,
  overlayRegisterKeybind: undefined,
  overlayUnregisterKeybind: undefined,
  browserPickRespond: undefined,
  // Renderer logs stay local rather than crossing the wire.
  logConsoleEntry: undefined,
  logRendererError: undefined,
  // Updating the desktop out from under a live remote session.
  getAppVersion: 'mobile',
  getIsDev: false,
  getAutoUpdate: false,
  checkForUpdates: undefined,
  startUpdate: undefined,
  applyPendingUpdate: undefined,
  markUpdaterDone: undefined,
  // Webview registration is how the *desktop* tells main which <webview>
  // backs a tab. Mobile has no <webview>; it views the desktop's through
  // subscribeWebview below.
  registerWebviewId: undefined,
  unregisterWebviewId: undefined,
}

/**
 * Surface that must be genuinely ABSENT on mobile, not stubbed.
 *
 * DESKTOP_ONLY entries are installed as `() => Promise.resolve(value)` — still
 * a function, and one whose return value is a truthy Promise. That is fine for
 * fire-and-forget calls, but wrong for anything a caller feature-detects with
 * `typeof api.x === 'function'` and then branches on the RESULT of: the stub
 * passes the check and returns truthy, so the caller concludes the work was
 * done. The `sendSync` quit-flushes are exactly that shape — a stub would make
 * the renderer skip persisting tabs entirely on mobile. Leaving the property
 * undefined makes feature detection honest.
 */
const OMITTED_ON_MOBILE = new Set<string>([
  // The phone has no ptys of its own; the desktop's map is the only one
  // that means anything, and it is refused over the relay in any case.
  'share.registerPtyMap',
  // Blocking flush. A relay round-trip cannot be synchronous, and a remote
  // client is not the machine being force-quit by an installer. Callers fall
  // back to the async `saveTabGroups`.
  'saveTabGroupsSync',
])

/**
 * Relay-only additions with no preload counterpart — they drive the desktop's
 * webview mirror from the phone. `app/src` reaches them through optional
 * chaining so they stay absent (and inert) under Electron.
 */
const RELAY_ONLY_METHODS = [
  'getStateSnapshot',
  'mobileActiveTab',
  'subscribeWebview',
  'unsubscribeWebview',
  'webviewInput',
] as const

const RELAY_ONLY_EVENTS: Record<string, string> = {
  onWebviewFrame: 'webview-frame',
}

/** Presence frame types the shim subscribes to on the raw transport. */
export const PRESENCE_EVENT_TYPES = ['cursor', 'typing-claim', 'typing-release'] as const

// Desktop IPC covers everything from a store read to a git clone; the ceiling
// is a stuck main process, not a slow call.
const REQUEST_TIMEOUT_MS = 60_000

function invoke(transport: ApiShimTransport, channel: string) {
  return (...args: unknown[]) => transport.request({ type: channel, args }, REQUEST_TIMEOUT_MS)
}

/**
 * Subscribe to a mirrored main→renderer channel. The desktop forwards the
 * `webContents.send` argument list verbatim, so callbacks receive the same
 * positional arguments Electron would deliver.
 */
function subscribe(transport: ApiShimTransport, channel: string) {
  return (callback: (...args: unknown[]) => void) => {
    const handler = (args: unknown[]) => callback(...(Array.isArray(args) ? args : [args]))
    transport.on(`ipc:${channel}`, handler)
    return () => transport.off(`ipc:${channel}`, handler)
  }
}

// ── PTY fan-out ────────────────────────────────────────────────────────

type PtyDataCb = (ptyId: string, data: string) => void

const EARLY_PTY_MAX_BYTES = 8 * 1024 * 1024

/**
 * Mirrors the preload's pty demux (electron/preload/index.ts). One relay
 * subscription feeds every terminal, so a chunk only wakes its own pty's
 * listeners instead of all N mounted terminals — the difference is quadratic
 * with a handful of sessions open.
 */
function createPtyFanout(transport: ApiShimTransport) {
  const byId = new Map<string, Set<PtyDataCb>>()
  const all = new Set<PtyDataCb>()

  // Chunks that arrive before a terminal has registered its listener. On
  // resume the desktop replays the transcript while the mobile component is
  // still mounting; without this buffer the opening bytes — screen setup and
  // the start of a cursor-addressed render — are dropped, and the TUI's diff
  // rendering desyncs from the real screen for the rest of the session.
  const early = new Map<string, { chunks: string[]; bytes: number }>()

  // Subscribed as the shim is built, not on first use: a resumed session
  // starts replaying the moment the desktop spawns it, well before React has
  // mounted the terminal that will read it. Deferring this would leave those
  // first chunks with nowhere to land.
  transport.on('ipc:pty-data', (args: unknown[]) => {
    const [ptyId, data] = args as [string, string]
    const set = byId.get(ptyId)
    if (set) {
      for (const cb of set) { try { cb(ptyId, data) } catch { /* ignore */ } }
    } else {
      let buf = early.get(ptyId)
      if (!buf) { buf = { chunks: [], bytes: 0 }; early.set(ptyId, buf) }
      if (buf.bytes < EARLY_PTY_MAX_BYTES) { buf.chunks.push(data); buf.bytes += data.length }
    }
    for (const cb of all) { try { cb(ptyId, data) } catch { /* ignore */ } }
  })
  // A pty that exits with no listener would otherwise leak its buffer.
  transport.on('ipc:pty-exit', (args: unknown[]) => { early.delete((args as [string])[0]) })

  return {
    onPtyData(callback: PtyDataCb): () => void {
      all.add(callback)
      return () => { all.delete(callback) }
    },

    onPtyDataFor(ptyId: string, callback: PtyDataCb): () => void {
      let set = byId.get(ptyId)
      const isFirstListener = !set
      if (!set) { set = new Set(); byId.set(ptyId, set) }
      set.add(callback)

      // Flush what was buffered before this listener existed, in order and
      // ahead of live chunks — live delivery only starts once the set exists.
      if (isFirstListener) {
        const buf = early.get(ptyId)
        if (buf) {
          early.delete(ptyId)
          for (const chunk of buf.chunks) { try { callback(ptyId, chunk) } catch { /* ignore */ } }
        }
      }

      return () => {
        const s = byId.get(ptyId)
        if (s) { s.delete(callback); if (s.size === 0) byId.delete(ptyId) }
      }
    },
  }
}

// ── Assembly ───────────────────────────────────────────────────────────

function setPath(root: Record<string, any>, path: string, value: unknown): void {
  const parts = path.split('.')
  const leaf = parts.pop()!
  let node = root
  for (const part of parts) {
    if (typeof node[part] !== 'object' || node[part] === null) node[part] = {}
    node = node[part]
  }
  node[leaf] = value
}

/**
 * Build a `window.api` stand-in bound to `transport`.
 *
 * Instantiable on purpose: the mobile client passes its relay singleton, and a
 * shared tab passes its own per-session transport. Nothing here touches
 * process globals — see installApiShim() in mobile/src/api-shim.ts for those.
 */
export function createApiShim(transport: ApiShimTransport): Record<string, any> {
  const api: Record<string, any> = {}
  const pty = createPtyFanout(transport)

  /** Properties the generator flags as `custom` — hand-written here. */
  const custom: Record<string, unknown> = {
    onPtyData: pty.onPtyData,
    onPtyDataFor: pty.onPtyDataFor,
    // Electron resolves a dropped File to a real path; a phone has none.
    getPathForFile: () => '',
  }

  for (const entry of PRELOAD_SURFACE) {
    if (OMITTED_ON_MOBILE.has(entry.path)) continue
    if (entry.path in DESKTOP_ONLY) {
      const value = DESKTOP_ONLY[entry.path]
      setPath(api, entry.path, () => Promise.resolve(value))
      continue
    }
    if (entry.path in custom) {
      setPath(api, entry.path, custom[entry.path])
      continue
    }

    switch (entry.kind) {
      case 'invoke':
        setPath(api, entry.path, invoke(transport, entry.channel!))
        break
      case 'on':
        setPath(api, entry.path, subscribe(transport, entry.channel!))
        break
      case 'send':
        // The three `send` channels are all window chrome.
        setPath(api, entry.path, () => undefined)
        break
      case 'custom':
        // Every custom property is either desktop-only or hand-written above.
        // Reaching here means the preload grew one we haven't handled: fail
        // loudly at the call site rather than returning a silent undefined.
        setPath(api, entry.path, () => {
          throw new Error(`[api-shim] ${entry.path} has no mobile implementation`)
        })
        break
    }
  }

  for (const name of RELAY_ONLY_METHODS) api[name] = invoke(transport, toChannel(name))

  // ── Presence ─────────────────────────────────────────────────────────
  //
  // Overrides whatever the PRELOAD_SURFACE loop installed for these two, and
  // deliberately does NOT use `invoke`. A presence frame has to reach the
  // worker with its own `type` at the top level, because that is what the
  // worker's VIEWER_SENDABLE check reads — wrapped in a request envelope it
  // would be dropped for every read-only guest, i.e. exactly the people
  // presence is for. Fire-and-forget: no requestId, no response, no timeout.
  api.presenceSend = (msg: unknown) => {
    transport.send(msg)
  }
  api.onPresenceMessage = (callback: (msg: unknown) => void) => {
    const handler = (msg: unknown) => callback(msg)
    for (const type of PRESENCE_EVENT_TYPES) transport.on(type, handler)
    return () => {
      for (const type of PRESENCE_EVENT_TYPES) transport.off(type, handler)
    }
  }
  for (const [name, channel] of Object.entries(RELAY_ONLY_EVENTS)) {
    api[name] = (callback: (data: unknown) => void) => {
      const handler = (data: unknown) => callback(data)
      transport.on(channel, handler)
      return () => transport.off(channel, handler)
    }
  }

  return api
}

/** camelCase → kebab-case, for the relay-only channels. */
function toChannel(name: string): string {
  return name.replace(/([A-Z])/g, '-$1').toLowerCase()
}

